import type { Application } from 'pixi.js';
import type { Palette } from '../palette/palettes';
import { AlignScene, DEFAULT_ALIGN_TUNING, type AlignTuning } from '../scenes/AlignScene';
import { ContactScene } from '../scenes/ContactScene';
import { SquareScaleTransition } from '../transitions/SquareScaleTransition';
import { activateCustomCursor } from '../ui/customCursor';

export type SceneId = 'ALIGN' | 'TRANSITION' | 'CONTACT';

export class SceneController {
  private state: SceneId = 'ALIGN';
  private align: AlignScene | null = null;
  private contact: ContactScene | null = null;
  private transition: SquareScaleTransition | null = null;
  private w = 1;
  private h = 1;
  private reducedMotion = false;
  tuning: AlignTuning = { ...DEFAULT_ALIGN_TUNING };

  constructor(
    private readonly app: Application,
    private palette: Palette,
  ) {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.startAlign();
  }

  get current(): SceneId {
    return this.state;
  }

  get alignScene(): AlignScene | null {
    return this.align;
  }

  get paletteId(): string {
    return this.palette.id;
  }

  get currentPalette(): Palette {
    return this.palette;
  }

  /** Debug: apply a new palette and restart ALIGN. */
  applyPaletteAndReset(palette: Palette): void {
    this.palette = palette;
    this.contact?.destroy();
    this.contact = null;
    this.transition?.destroy();
    this.transition = null;
    this.startAlign();
    this.align?.resize(this.w, this.h);
  }

  resize(w: number, h: number): void {
    this.w = w;
    this.h = h;
    this.align?.resize(w, h);
    this.contact?.resize(w, h);
  }

  update(dt: number): void {
    if (this.state === 'ALIGN') {
      this.align?.update(dt);
    } else if (this.state === 'TRANSITION') {
      this.updateAlignTransition(dt);
    } else {
      this.contact?.update(dt);
    }
  }

  skipToContact(): void {
    if (this.state === 'CONTACT') return;
    if (this.state === 'ALIGN' && this.align) {
      this.align.forceComplete();
      return;
    }
    this.enterContact();
  }

  /** Debug-only: rebuild ALIGN scene. */
  resetAlign(): void {
    if (this.state !== 'ALIGN' || !this.align) {
      this.contact?.destroy();
      this.contact = null;
      this.transition?.destroy();
      this.transition = null;
      this.startAlign();
      this.align?.resize(this.w, this.h);
      return;
    }
    this.align.resetScene();
  }

  destroy(): void {
    this.align?.destroy();
    this.contact?.destroy();
    this.transition?.destroy();
  }

  private startAlign(): void {
    this.state = 'ALIGN';
    this.align?.destroy();
    this.align = new AlignScene(
      this.app,
      this.palette,
      this.tuning,
      {
        onSolved: (x, y, angle) => this.beginTransition(x, y, angle),
      },
      document.getElementById('hint'),
    );
    this.align.resize(this.w || window.innerWidth, this.h || window.innerHeight);
    document.body.style.overflow = 'hidden';
    this.app.canvas.style.pointerEvents = 'auto';
    this.app.canvas.style.touchAction = 'none';
    this.app.canvas.style.cursor = 'none';
    this.setAlignGuideVisible(true);
    activateCustomCursor();
  }

  private createContact(): ContactScene {
    return new ContactScene(this.app, this.palette);
  }

  private beginTransition(x: number, y: number, angle: number): void {
    this.state = 'TRANSITION';
    this.app.stage.sortableChildren = true;

    this.contact?.destroy();
    this.contact = this.createContact();
    this.contact.resize(this.w, this.h);
    this.contact.show({ deferText: true });
    this.contact.root.zIndex = 500;

    if (this.align) {
      this.align.root.zIndex = 100;
    }

    this.transition?.destroy();
    this.transition = new SquareScaleTransition();
    const sourceSize = this.align?.snapSize ?? 72;
    this.transition.begin(
      x,
      y,
      this.w,
      this.h,
      this.palette.fieldA,
      this.palette.fieldB,
      angle,
      sourceSize,
      this.reducedMotion,
    );
    this.transition.root.zIndex = 1000;
    this.app.stage.addChild(this.transition.root);
    this.transition.update(0);
  }

  private updateAlignTransition(dt: number): void {
    if (!this.transition) {
      this.enterContact(true);
      return;
    }

    const done = this.transition.update(dt);
    if (done) {
      this.contact?.beginTextReveal();
      this.transition.destroy();
      this.transition = null;
      this.enterContact(true);
    }
  }

  private setAlignGuideVisible(visible: boolean): void {
    const guide = document.getElementById('align-guide');
    if (!guide) return;
    guide.hidden = !visible;
    if (visible) guide.classList.remove('is-dismissed');
  }

  private enterContact(fromTransition = false): void {
    this.state = 'CONTACT';
    this.setAlignGuideVisible(false);
    this.align?.destroy();
    this.align = null;
    this.transition?.destroy();
    this.transition = null;

    if (!this.contact) {
      this.contact = this.createContact();
      this.contact.resize(this.w, this.h);
    }
    if (!fromTransition) {
      this.contact.show();
    } else {
      this.contact.finishTransitionShow();
    }
    this.app.canvas.style.cursor = 'auto';
  }
}
